
import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, isOnline, registerChannel, cleanupChannel } from "@/integrations/supabase/client";
import { Group } from "@/types/group";
import { useToast } from "@/components/ui/use-toast";
import { useTranslation } from "react-i18next";
import { safeSelectArray } from "@/utils/supabaseHelpers";

export const useGroups = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const MAX_RETRIES = 5; 
  const RETRY_DELAY = 1500; 
  const lastFetchTimeRef = useRef<number>(0);
  const cachedGroupsRef = useRef<{data: Group[], timestamp: number} | null>(null);
  const isMounted = useRef(true);
  const isSubscribingRef = useRef(false);
  const fetchInProgressRef = useRef(false);
  const debounceFetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Cache expiration time in milliseconds (30 minutes - extended for better performance)
  const CACHE_EXPIRATION = 30 * 60 * 1000;

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    return (
      cachedGroupsRef.current !== null &&
      Date.now() - cachedGroupsRef.current.timestamp < CACHE_EXPIRATION
    );
  }, []);

  // Fetch all groups the user is a member of with improved throttling
  const fetchGroups = useCallback(async (forceFresh = false) => {
    // Don't fetch if already in progress to avoid race conditions
    if (fetchInProgressRef.current) {
      console.log("Fetch already in progress, skipping redundant fetch");
      return;
    }
    
    // Don't fetch if already loading and recent fetch occurred
    const now = Date.now();
    const throttleTime = 7500; // 7.5 seconds between fetches to prevent stuttering
    if (!forceFresh && now - lastFetchTimeRef.current < throttleTime) {
      console.log("Throttling group fetch - too soon since last fetch");
      
      // Schedule a fetch for later if needed
      if (debounceFetchTimeoutRef.current) {
        clearTimeout(debounceFetchTimeoutRef.current);
      }
      
      debounceFetchTimeoutRef.current = setTimeout(() => {
        fetchGroups(true);
      }, throttleTime - (now - lastFetchTimeRef.current));
      
      return;
    }
    
    // Use cached data if available and not force refreshing
    if (!forceFresh && isCacheValid() && cachedGroupsRef.current) {
      console.log("Using cached groups data");
      if (isMounted.current) {
        setGroups(cachedGroupsRef.current.data);
        setLoading(false);
        setError(false);
        setInitialized(true);
      }
      return;
    }
    
    try {
      fetchInProgressRef.current = true;
      
      if (isMounted.current) {
        lastFetchTimeRef.current = now;
        setLoading(true);
        setError(false);
        console.log("Fetching groups...");
      }

      // Check if offline and we have cache
      if (!isOnline() && cachedGroupsRef.current) {
        console.log("Offline mode: using cached data");
        if (isMounted.current) {
          setGroups(cachedGroupsRef.current.data);
          setLoading(false);
          setInitialized(true);
        }
        fetchInProgressRef.current = false;
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log("No session found");
        if (isMounted.current) {
          setGroups([]);
          setLoading(false);
          setInitialized(true);
        }
        fetchInProgressRef.current = false;
        return;
      }

      try {
        // Using our safeSelectArray helper for better error handling
        const response = await safeSelectArray<any>(
          'group_members',
          { profile_id: session.user.id },
          {
            select: `
              group_id,
              joined_at,
              groups:group_id(
                id,
                name,
                created_at,
                status,
                created_by
              )
            `,
            order: {
              column: 'joined_at',
              ascending: false
            }
          }
        );

        if (response.success && response.data && response.data.length > 0 && isMounted.current) {
          // Extract and format the groups
          const formattedGroups = response.data
            .filter(item => item.groups && item.groups.status === 'active')
            .map(item => ({
              id: item.groups?.id || "",
              name: item.groups?.name || "",
              created_at: item.groups?.created_at || "",
              status: item.groups?.status || "active",
              created_by: item.groups?.created_by || ""
            } as Group));

          // Update cache
          cachedGroupsRef.current = {
            data: formattedGroups,
            timestamp: Date.now()
          };

          console.log("Groups loaded:", formattedGroups.length);
          
          if (isMounted.current) {
            setGroups(formattedGroups);
            setLoading(false);
            setError(false);
            setInitialized(true);
            setRetryCount(0); // Reset retry count on success
            
            // Save to localStorage as additional backup
            try {
              localStorage.setItem('cached-groups', JSON.stringify({
                data: formattedGroups,
                timestamp: Date.now()
              }));
            } catch (e) {
              console.warn('Could not save groups to localStorage', e);
            }
          }
        } else if (isMounted.current) {
          setGroups([]);
          setLoading(false);
          setInitialized(true);
        }
      } catch (error) {
        console.error("Error in fetchGroups:", error);
        
        if (retryCount < MAX_RETRIES && isMounted.current) {
          console.log(`Retrying due to error (${retryCount + 1}/${MAX_RETRIES}) in ${RETRY_DELAY}ms...`);
          setRetryCount(prev => prev + 1);
          setTimeout(() => fetchGroups(forceFresh), RETRY_DELAY * Math.pow(1.5, retryCount));
          fetchInProgressRef.current = false;
          return;
        }
        
        if (isMounted.current) {
          setError(true);
          
          // Only show toast on first load, not on refresh attempts
          if (!initialized) {
            toast({
              title: t('common.error', 'Error'),
              description: t('groups.loadError', 'Error loading groups. Please try refreshing the page.'),
              variant: "destructive",
            });
          }
          
          // If we have cached data, fall back to it
          if (cachedGroupsRef.current) {
            console.log("Falling back to cached groups data after error");
            setGroups(cachedGroupsRef.current.data);
          } else {
            // Try to get data from localStorage as last resort
            try {
              const storedData = localStorage.getItem('cached-groups');
              if (storedData) {
                const parsed = JSON.parse(storedData);
                setGroups(parsed.data || []);
                console.log("Retrieved groups from localStorage");
              } else {
                setGroups([]);
              }
            } catch (e) {
              console.warn('Could not retrieve groups from localStorage', e);
              setGroups([]);
            }
          }
          
          setLoading(false);
          setInitialized(true);
        }
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      if (isMounted.current) {
        setLoading(false);
        setError(true);
        if (cachedGroupsRef.current) {
          setGroups(cachedGroupsRef.current.data);
        } else {
          setGroups([]);
        }
      }
    } finally {
      fetchInProgressRef.current = false;
    }
  }, [toast, t, retryCount, isCacheValid, initialized]);

  // Set up subscriptions with improved handling to prevent excessive events
  const setupSubscriptions = useCallback(() => {
    if (isSubscribingRef.current) return null;
    isSubscribingRef.current = true;
    
    console.log("Setting up group subscriptions...");
    
    // Much heavier throttling for subscription updates
    let lastSubscriptionUpdateTime = 0;
    const handleSubscriptionEvent = () => {
      const now = Date.now();
      // Only allow subscription updates every 10 seconds to prevent UI flutter
      if (now - lastSubscriptionUpdateTime < 10000) {
        console.log("Throttling subscription update - too frequent");
        return;
      }
      
      lastSubscriptionUpdateTime = now;
      console.log("Processing subscription update event");
      
      // Use the current time to determine if we need to fetch fresh data
      const timeSinceLastFetch = now - lastFetchTimeRef.current;
      if (timeSinceLastFetch > 10000) {
        // It's been more than 10 seconds since our last fetch, do it now
        fetchGroups(true);
      } else {
        console.log(`Last fetch was only ${timeSinceLastFetch}ms ago, scheduling future fetch`);
        // Schedule a future fetch
        if (debounceFetchTimeoutRef.current) {
          clearTimeout(debounceFetchTimeoutRef.current);
        }
        debounceFetchTimeoutRef.current = setTimeout(() => {
          fetchGroups(true);
        }, 10000 - timeSinceLastFetch);
      }
    };
    
    // Set up a real-time subscription for group changes
    registerChannel(
      'group-changes',
      supabase.channel('group-changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'groups'
        }, () => {
          handleSubscriptionEvent();
        })
        .subscribe((status) => {
          console.log(`Groups subscription status: ${status}`);
        })
    );
    
    registerChannel(
      'group-member-changes',
      supabase.channel('group-member-changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'group_members'
        }, () => {
          handleSubscriptionEvent();
        })
        .subscribe((status) => {
          console.log(`Group members subscription status: ${status}`);
        })
    );

    return () => {
      console.log("Cleaning up group subscriptions...");
      cleanupChannel('group-changes');
      cleanupChannel('group-member-changes');
      isSubscribingRef.current = false;
    };
  }, [fetchGroups]);

  // Check for stored data on initial load
  useEffect(() => {
    if (!cachedGroupsRef.current) {
      try {
        const storedData = localStorage.getItem('cached-groups');
        if (storedData) {
          const parsed = JSON.parse(storedData);
          if (parsed && Date.now() - parsed.timestamp < CACHE_EXPIRATION) {
            cachedGroupsRef.current = parsed;
            console.log("Restored groups from localStorage cache");
          }
        }
      } catch (e) {
        console.warn('Error loading cached groups from localStorage', e);
      }
    }
  }, []);

  // Initial fetch and subscription setup
  useEffect(() => {
    isMounted.current = true;
    fetchGroups();
    
    // Add online event listener for better offline support
    const handleOnline = () => {
      console.log("Device is back online, refreshing groups...");
      // Reset retry count when coming back online
      setRetryCount(0);
      setError(false);
      fetchGroups(true); // Force fresh data
    };
    
    window.addEventListener('online', handleOnline);
    
    // Set up subscriptions
    const cleanup = setupSubscriptions();
    
    return () => {
      isMounted.current = false;
      window.removeEventListener('online', handleOnline);
      
      if (cleanup) cleanup();
      
      if (debounceFetchTimeoutRef.current) {
        clearTimeout(debounceFetchTimeoutRef.current);
        debounceFetchTimeoutRef.current = null;
      }
    };
  }, [fetchGroups, setupSubscriptions]);

  // Session auth state monitoring
  useEffect(() => {
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        if (!session && isMounted.current) {
          navigate('/login');
        } else if (session && isMounted.current) {
          // Refresh groups when session changes
          fetchGroups(true);
        }
      }
    );

    return () => {
      if (authSubscription) authSubscription.unsubscribe();
    };
  }, [navigate, fetchGroups]);

  const handleGroupClick = useCallback((groupId: string) => {
    navigate(`/group/${groupId}`);
  }, [navigate]);

  const refreshGroups = useCallback(() => {
    // Prevent rapid refresh clicks
    const now = Date.now();
    if (now - lastFetchTimeRef.current < 3000) {
      console.log("Throttling manual refresh - too soon");
      return;
    }
    
    console.log("Manually refreshing groups...");
    setError(false); // Reset error state on manual refresh
    setRetryCount(0); // Reset retry count on manual refresh
    fetchGroups(true); // Force fresh data
  }, [fetchGroups]);

  return {
    groups,
    loading,
    error,
    initialized,
    handleGroupClick,
    refreshGroups
  };
};
